'use client';

import type { DocumentNode } from 'graphql';
import { GraphQLClient } from 'graphql-request';

import { getStoredProfile, nowInSeconds, refreshToken } from '@/services/auth';

import { log } from '../appMonitoringSuite';
import { getQueryString } from './utils';

const NEXT_PUBLIC_RTE_URL = process.env.NEXT_PUBLIC_RTE_URL ?? '';
const PLATFORM = 'platform_html';

const createGraphQLClient = () => {
  const client = new GraphQLClient(NEXT_PUBLIC_RTE_URL, {
    requestMiddleware: async (request) => {
      const storedProfile = getStoredProfile();
      let token = storedProfile?.accessToken;
      const expires = storedProfile?.expires;

      if (token && expires !== undefined && nowInSeconds() >= expires) {
        try {
          log('GRAPHQL-AUTH-MIDDLEWARE: Refresh the tokens', 'info');
          const refreshedProfile = await refreshToken();
          if (refreshedProfile?.access_token) {
            token = refreshedProfile.access_token;
          }
        } catch (error) {
          console.error('Failed to refresh token:', error);
          log('GRAPHQL-AUTH-MIDDLEWARE: Refresh Token Fetch failed', 'error');
          // A 429 is handled by refreshToken(); re-throw it instead of treating it as an expired session.
          if (error instanceof Error && error.message === '429') {
            throw error;
          }
          throw new Error('Session expired. Please log in again.');
        }
      }

      const authPayload = token
        ? { platform: PLATFORM, accessToken: token }
        : { platform: PLATFORM };
      const authToken = btoa(JSON.stringify(authPayload));

      // graphql-request hands the middleware a Headers instance; spreading it yields {}
      // and drops per-request headers such as x-dco-correlation-id.
      const headers = new Headers(request.headers as HeadersInit);
      headers.set('Authorization', authToken);
      headers.set('Content-Type', 'application/json');
      headers.set('x-client-platform', PLATFORM);

      return {
        ...request,
        headers,
      };
    },
    errorPolicy: 'all',
  });

  return client;
};

export const graphQLClient = createGraphQLClient();

export const requestGraphQL = async <T, V = Record<string, unknown>>(
  document: DocumentNode | string,
  variables?: V,
  requestHeaders?: HeadersInit,
  options?: {
    // Some paginated queries (for example screen rows) can return usable `data`
    // alongside GraphQL row-level errors. Allow callers to opt into consuming
    // that partial data instead of dropping the entire page.
    allowPartialData?: boolean;
  }
): Promise<T> => {
  const query = getQueryString(document);

  const response = await graphQLClient.rawRequest(
    query,
    variables as Record<string, unknown>,
    requestHeaders
  );

  const realErrors = (response.errors || []).filter((err) => {
    const errorType: string = (err as any).errorType || '';
    return !['DEGRADATION', 'WARNING'].includes(errorType);
  });

  if (realErrors.length > 0) {
    // Keep partial payload when explicitly allowed by caller.
    if (options?.allowPartialData && response.data) {
      return response.data as T;
    }
    throw new Error('GRAPHQL Server Errors. Please observe the logs');
  }

  return response.data;
};

/**
 * execute GraphQL request and return full response including headers.
 * used when response headers are needed.
 */
export const requestGraphQLWithResponse = async <T, V = Record<string, unknown>>(
  document: DocumentNode | string,
  variables?: V,
  requestHeaders?: HeadersInit
): Promise<{ data: T; headers: Headers; status: number }> => {
  const query = getQueryString(document);

  const response = await graphQLClient.rawRequest<T, Record<string, unknown>>(
    query,
    variables as Record<string, unknown>,
    requestHeaders
  );

  const realErrors = (response.errors || []).filter((err) => {
    const errorType: string = (err as any).errorType || '';
    return errorType !== 'WARNING';
  });

  if (realErrors.length > 0) {
    throw new Error('GRAPHQL Server Errors. Please observe the logs');
  }

  return {
    data: response.data,
    headers: response.headers,
    status: response.status,
  };
};
